import { toast, type Id, type ToastContent, type ToastOptions } from 'react-toastify';

const BASE_OPTIONS: ToastOptions = {
  position: 'top-right',
  closeButton: true,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  draggable: true,
};

const mergeOptions = (autoClose: number, options?: ToastOptions): ToastOptions => ({
  ...BASE_OPTIONS,
  autoClose,
  ...options,
  style: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    ...(options?.style || {}),
  },
});

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null;

type NotifyAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'send'
  | 'complete'
  | 'finish'
  | 'fetch';

/**
 * ⚠️ `subject` é um SUBSTANTIVO, não uma frase (at-65, BUG-V51).
 *
 * `actionSuccess`/`actionError` conjugam o verbo a partir do `action`, **sempre no
 * masculino singular** — o texto final é "{subject} {verbo} com sucesso". Passar frase já
 * conjugada gera "Atividade finalizada concluído com sucesso"; passar substantivo
 * feminino ou plural gera "Horas reprovado", "Especialidade criado".
 *
 * Regra prática: substantivo masculino singular → use estes helpers. Qualquer outro caso
 * (frase pronta, feminino, plural) → `notify.success(...)` com o texto escrito à mão.
 * Sete chamadas nasceram erradas por causa disso, em 5 arquivos.
 */
type NotifyContext = {
  action: NotifyAction;
  subject?: string;
};

const extractStatus = (error: unknown): number | undefined => {
  if (!isRecord(error)) return undefined;
  const response = error.response;
  if (!isRecord(response)) return undefined;
  return typeof response.status === 'number' ? response.status : undefined;
};

const successByContext = ({ action, subject }: NotifyContext): string => {
  const item = subject?.trim() || 'Operação';
  switch (action) {
    case 'create':
      return `${item} criado com sucesso.`;
    case 'update':
      return `${item} atualizado com sucesso.`;
    case 'delete':
      return `${item} removido com sucesso.`;
    case 'approve':
      return `${item} aprovado com sucesso.`;
    case 'reject':
      return `${item} reprovado com sucesso.`;
    case 'send':
      return `${item} enviado com sucesso.`;
    case 'complete':
    case 'finish':
      return `${item} concluído com sucesso.`;
    case 'fetch':
      return `${item} carregado com sucesso.`;
    default:
      return 'Operação concluída com sucesso.';
  }
};

const fallbackErrorByContext = ({ action, subject }: NotifyContext): string => {
  const item = subject?.trim() || 'a ação';
  switch (action) {
    case 'create':
      return `Não foi possível criar ${item}.`;
    case 'update':
      return `Não foi possível atualizar ${item}.`;
    case 'delete':
      return `Não foi possível remover ${item}.`;
    case 'approve':
      return `Não foi possível aprovar ${item}.`;
    case 'reject':
      return `Não foi possível reprovar ${item}.`;
    case 'send':
      return `Não foi possível enviar ${item}.`;
    case 'complete':
    case 'finish':
      return `Não foi possível concluir ${item}.`;
    case 'fetch':
      return `Não foi possível carregar ${item}.`;
    default:
      return 'Não foi possível concluir a ação.';
  }
};

const messageFromUnknown = (error: unknown, fallback: string): string => {
  if (typeof error === 'string' && error.trim()) return error;

  if (isRecord(error)) {
    const response = error.response;
    if (isRecord(response)) {
      const data = response.data;
      const status = typeof response.status === 'number' ? response.status : undefined;

      if (isRecord(data) && typeof data.message === 'string' && data.message.trim()) {
        return data.message;
      }

      if (Array.isArray((data as any)?.message) && (data as any).message.length > 0) {
        return (data as any).message.join(', ');
      }

      if (status === 401) return 'Sessão expirada. Faça login novamente.';
      if (status === 403) return 'Você não tem permissão para executar esta ação.';
      if (status !== undefined && status >= 500) return 'Erro interno do servidor. Tente novamente em instantes.';
    }

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
  }

  return fallback;
};

/**
 * Mesma extração usada pelos toasts, para quem precisa do texto e não do toast (at-64).
 *
 * Existe porque aviso inline e toast têm de dizer a MESMA coisa: o toast some em segundos
 * e o inline é o que sobra na tela. Sem isto, quem escreve o banner recorre a texto fixo
 * genérico e a explicação do backend (ex: o 422 de "consultor sem código cadastrado")
 * se perde justamente no sinal permanente.
 */
export const mensagemDeErro = (error: unknown, fallback: string): string =>
  messageFromUnknown(error, fallback);

let isConfigured = false;

export const configureGlobalNotify = () => {
  if (isConfigured) return;
  isConfigured = true;

  const originalSuccess = toast.success.bind(toast);
  const originalInfo = toast.info.bind(toast);
  const originalWarn = toast.warn.bind(toast);
  const originalError = toast.error.bind(toast);

  (toast as any).success = (content: ToastContent<unknown>, options?: ToastOptions) =>
    originalSuccess(content, mergeOptions(3000, options));

  (toast as any).info = (content: ToastContent<unknown>, options?: ToastOptions) =>
    originalInfo(content, mergeOptions(3000, options));

  (toast as any).warn = (content: ToastContent<unknown>, options?: ToastOptions) =>
    originalWarn(content, mergeOptions(5000, options));

  (toast as any).warning = (content: ToastContent<unknown>, options?: ToastOptions) =>
    originalWarn(content, mergeOptions(5000, options));

  (toast as any).error = (content: ToastContent<unknown>, options?: ToastOptions) =>
    originalError(content, mergeOptions(5000, options));
};

export const notify = {
  success: (message: ToastContent<unknown>, options?: ToastOptions): Id =>
    toast.success(message, mergeOptions(3000, options)),

  info: (message: ToastContent<unknown>, options?: ToastOptions): Id =>
    toast.info(message, mergeOptions(3000, options)),

  warning: (message: ToastContent<unknown>, options?: ToastOptions): Id =>
    toast.warn(message, mergeOptions(5000, options)),

  warn: (message: ToastContent<unknown>, options?: ToastOptions): Id =>
    toast.warn(message, mergeOptions(5000, options)),

  error: (message: ToastContent<unknown>, options?: ToastOptions): Id =>
    toast.error(message, mergeOptions(5000, options)),

  // `options` existe para o chamador poder passar `toastId` em falha recorrente
  // (polling, laço por veículo, evento de websocket) sem abrir mão da mensagem que
  // veio do erro — sem ele, deduplicar exigia trocar `errorFrom` por `error` com
  // texto fixo. Acrescentado na at-60.
  errorFrom: (error: unknown, fallback = 'Não foi possível concluir a ação.', options?: ToastOptions): Id =>
    toast.error(messageFromUnknown(error, fallback), mergeOptions(5000, options)),

  actionSuccess: (context: NotifyContext, options?: ToastOptions): Id =>
    toast.success(successByContext(context), mergeOptions(3000, options)),

  actionError: (error: unknown, context: NotifyContext, options?: ToastOptions): Id => {
    const status = extractStatus(error);
    const fallback = fallbackErrorByContext(context);

    if (status === 400) {
      return toast.warn(messageFromUnknown(error, 'Verifique os dados informados e tente novamente.'), mergeOptions(5000, options));
    }

    if (status === 404) {
      return toast.warn('Registro não encontrado ou indisponível no momento.', mergeOptions(5000, options));
    }

    if (status === 409) {
      const msg = messageFromUnknown(error, '');
      if (msg) {
        return toast.warn(msg, mergeOptions(5000, options));
      }
      return toast.warn('Conflito de dados: este registro já existe ou foi alterado.', mergeOptions(5000, options));
    }

    return toast.error(messageFromUnknown(error, fallback), mergeOptions(5000, options));
  },

  dismiss: (id?: Id) => toast.dismiss(id),
};
